// WebSocket client (§20.1, §20.11, §23.5): hello handshake, reconnect with backoff and the room
// token, typed routing of server messages, and input batching. Incoming frames are queued and
// dispatched by `pump()` (once per frame) with their arrival time; `lagMs` (`?lag=MS`) delays both
// directions. DOM-free: the socket comes from an injectable factory, so Node can drive it too.
import type { Cosmetics } from '@shared/cosmetics';
import { BinWriter } from '@shared/protocol/binary';
import { encodeInputBatch } from '@shared/protocol/input';
import { parseServerMsg, type ClientMsg, type ServerMsg } from '@shared/protocol/messages';
import { BinMsg, INPUT_FRAME_BYTES, INPUT_HEADER_BYTES, INPUT_MAX_FRAMES } from '@shared/protocol/schema';
import { PROTOCOL_VERSION } from '@shared/protocol/version';
import type { InputFrame } from '@shared/types';
import type { Bus, BusEvents } from '../core/bus';

// ---- Socket seam --------------------------------------------------------------------------------

export interface SocketHandlers {
  open(): void;
  /** A text frame as a string, a binary frame as an ArrayBuffer. */
  message(data: string | ArrayBuffer): void;
  close(): void;
}

export interface SocketLike {
  readonly isOpen: boolean;
  send(data: string | Uint8Array): void;
  close(): void;
}

/** Opens a socket to `url` and reports its events to `h`. */
export type SocketFactory = (url: string, h: SocketHandlers) => SocketLike;

/** The platform `WebSocket` (browsers, and Node ≥ 22 where it is global). */
export const webSocketFactory: SocketFactory = (url, h) => {
  const ws = new WebSocket(url);
  ws.binaryType = 'arraybuffer';
  ws.onopen = () => h.open();
  ws.onmessage = (ev: MessageEvent<string | ArrayBuffer>) => h.message(ev.data);
  ws.onclose = () => h.close();
  return {
    get isOpen() {
      return ws.readyState === WebSocket.OPEN;
    },
    send: (data) => ws.send(data),
    close: () => ws.close(),
  };
};

// ---- Reconnect session --------------------------------------------------------------------------

/** The room to rejoin after a reload or socket loss (§9.6). */
export interface RoomSession { code: string; token: string }

/** Where the session lives; the browser shell backs it with `sessionStorage`. */
export interface SessionStore { load(): RoomSession | null; save(s: RoomSession): void; clear(): void }

export function memorySessionStore(): SessionStore {
  let s: RoomSession | null = null;
  return {
    load: () => s,
    save: (v) => {
      s = { ...v };
    },
    clear: () => {
      s = null;
    },
  };
}

// ---- Net ----------------------------------------------------------------------------------------

export const NET = {
  /** Reconnect delays, ms; the last one repeats (§9.6). */
  backoffMs: [500, 1000, 2000, 4000, 8000],
  /** Control messages held while (re)connecting; older ones are dropped beyond this. */
  maxQueuedSends: 32,
  /** Received frames held for `pump()`; beyond this they are dispatched immediately. */
  maxQueuedFrames: 2048,
  snapshotBytesEwma: 0.1,
  /**
   * Dead-connection watchdog. In a room the server streams snapshots at 30 Hz and answers the
   * 1 s clock pings, so this much silence while online means the network went away without a TCP
   * close (Wi-Fi gone): drop the socket and reconnect with the room token instead of freezing
   * until the OS gives up on it (minutes).
   */
  silenceMs: 3500,
  /** A (re)connect that has not reached `welcome` this long after the socket opened is retried. */
  handshakeMs: 8000,
  /** Watchdog cadence; a check that runs this many intervals late (a stalled tab) is skipped. */
  watchdogMs: 500, watchdogStallIntervals: 3,
} as const;

export type NetStatus = BusEvents['net:status']['state'];

/** Routed messages: every server control message by `t`, plus raw snapshot frames. */
export type NetMessages = { [K in ServerMsg['t']]: Extract<ServerMsg, { t: K }> } & { snapshot: Uint8Array };
/** `at` is the arrival time (plus artificial lag), on the same clock as `NetOptions.now`. */
export type NetHandler<K extends keyof NetMessages> = (msg: NetMessages[K], at: number) => void;

/** Player identity sent with every `hello`. */
export interface HelloInfo { name: string; profileId: string; cosmetics: Cosmetics }

export interface NetStats { bytesIn: number; bytesOut: number; snapshots: number; snapBytesAvg: number; malformed: number }

/** What the game layer needs from the transport (`Net` implements it; tests may fake it). */
export interface NetClient {
  readonly status: NetStatus;
  readonly stats: Readonly<NetStats>;
  on<K extends keyof NetMessages>(type: K, fn: NetHandler<K>): () => void;
  send(msg: ClientMsg): boolean;
  sendInputs(frames: readonly InputFrame[], viewTick: number): boolean;
}

export interface NetOptions {
  /** `wss?://<host>/ws`. */
  url: string;
  bus: Bus;
  hello(): HelloInfo;
  socketFactory?: SocketFactory;
  session?: SessionStore;
  /** Artificial latency per direction, ms (`?lag=MS`). */
  lagMs?: number;
  /** Monotonic ms clock shared with the rest of the client. */
  now?: () => number;
  /**
   * Whether the device has a network (the browser shell passes its online flag; default: always).
   * While it has none, reconnect attempts are held (each would only fail, and browsers log every
   * failed WebSocket as a console error); `retryNow()` on the 'online' event resumes them at once.
   */
  isOnline?: () => boolean;
}

interface Incoming { type: keyof NetMessages; msg: unknown; at: number }

type AnyHandler = (msg: unknown, at: number) => void;

export class Net implements NetClient {
  readonly stats: NetStats = { bytesIn: 0, bytesOut: 0, snapshots: 0, snapBytesAvg: 0, malformed: 0 };
  private state: NetStatus = 'offline';
  private attempt = 0;
  private socket: SocketLike | null = null;
  /** Bumped per socket so events of a replaced or closed socket are ignored. */
  private generation = 0;
  private wanted = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnecting = false;
  private readonly handlers = new Map<keyof NetMessages, AnyHandler[]>();
  private readonly incoming: Incoming[] = [];
  private incomingHead = 0;
  private readonly outbox: string[] = [];
  private readonly writer = new BinWriter(INPUT_HEADER_BYTES + INPUT_MAX_FRAMES * INPUT_FRAME_BYTES);
  private readonly session: SessionStore;
  private readonly factory: SocketFactory;
  private readonly lagMs: number;
  private readonly now: () => number;
  private readonly isOnline: () => boolean;
  /** Arrival time of the newest frame (or of the socket's open), for the watchdog. */
  private lastRx = 0;
  private openedAt = 0;
  private watchdog: ReturnType<typeof setInterval> | null = null;
  private lastCheck = 0;

  constructor(private readonly opts: NetOptions) {
    this.session = opts.session ?? memorySessionStore();
    this.factory = opts.socketFactory ?? webSocketFactory;
    this.lagMs = Math.max(0, opts.lagMs ?? 0);
    this.now = opts.now ?? (() => performance.now());
    this.isOnline = opts.isOnline ?? (() => true);
  }

  get status(): NetStatus {
    return this.state;
  }

  /** The stored room session (rejoined automatically on the next connect). */
  get roomSession(): RoomSession | null {
    return this.session.load();
  }

  /** Opens the connection (no-op while connected or connecting); reconnects automatically until `close()`. */
  connect(): void {
    this.wanted = true;
    if (this.socket || this.reconnectTimer) return;
    this.setStatus(this.attempt > 0 ? 'reconnecting' : 'connecting');
    this.open();
  }

  /**
   * Skips the rest of a reconnect backoff and tries now (the browser came back online, the tab
   * became visible, or the player pressed "Reconnect"). No-op unless a reconnect is pending and
   * the device has a network.
   */
  retryNow(): void {
    if (!this.wanted || this.socket || !this.reconnectTimer || !this.isOnline()) return;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.open();
  }

  /**
   * The network is gone (the browser's 'offline' event, or the watchdog): drops the current socket
   * at once and reconnects with backoff, rejoining with the room token. No-op without a socket.
   */
  networkLost(): void {
    const s = this.socket;
    if (!s) return;
    this.generation++;
    s.close();
    this.onClosed();
  }

  /** Closes for good: no reconnect, queued sends are dropped. The room session is kept. */
  close(): void {
    this.stopWatchdog();
    this.wanted = false;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.outbox.length = 0;
    this.generation++;
    const s = this.socket;
    this.socket = null;
    s?.close();
    this.attempt = 0;
    this.setStatus('offline');
  }

  on<K extends keyof NetMessages>(type: K, fn: NetHandler<K>): () => void {
    const list = this.handlers.get(type) ?? [];
    const h = fn as AnyHandler;
    this.handlers.set(type, [...list, h]);
    return () => {
      const cur = this.handlers.get(type) ?? [];
      this.handlers.set(type, cur.filter((x) => x !== h));
    };
  }

  /**
   * Sends a control message. While (re)connecting it is held until the server's `welcome`;
   * offline it is dropped (returns false). `leaveRoom` also forgets the room session.
   */
  send(msg: ClientMsg): boolean {
    if (msg.t === 'leaveRoom') this.session.clear();
    const text = JSON.stringify(msg);
    if (this.state === 'online') {
      this.transmit(text);
      return true;
    }
    if (this.state === 'offline') return false;
    if (this.outbox.length >= NET.maxQueuedSends) this.outbox.shift();
    this.outbox.push(text);
    return true;
  }

  /** Sends one INPUT batch (1–8 frames, consecutive ticks). Dropped unless online. */
  sendInputs(frames: readonly InputFrame[], viewTick: number): boolean {
    if (this.state !== 'online' || frames.length === 0) return false;
    this.transmit(encodeInputBatch(frames, viewTick, this.writer).slice());
    return true;
  }

  /**
   * Dispatches every received frame that is due (all of them without artificial lag), in order.
   * Handlers are isolated like bus listeners: one that throws does not stop the others, and its
   * error is rethrown from a microtask.
   */
  pump(): void {
    const now = this.now();
    while (this.incomingHead < this.incoming.length && this.incoming[this.incomingHead].at <= now) {
      const m = this.incoming[this.incomingHead++];
      for (const h of this.handlers.get(m.type) ?? []) {
        try {
          h(m.msg, m.at);
        } catch (err) {
          queueMicrotask(() => {
            throw err;
          });
        }
      }
    }
    if (this.incomingHead === this.incoming.length) {
      this.incoming.length = 0;
      this.incomingHead = 0;
    } else if (this.incomingHead > 256) {
      this.incoming.splice(0, this.incomingHead);
      this.incomingHead = 0;
    }
  }

  private open(): void {
    const gen = ++this.generation;
    this.openedAt = this.lastRx = this.lastCheck = this.now();
    this.startWatchdog();
    this.socket = this.factory(this.opts.url, {
      open: () => {
        if (gen === this.generation) this.sendHello();
      },
      message: (data) => {
        if (gen === this.generation) this.receive(data);
      },
      close: () => {
        if (gen !== this.generation) return;
        // A socket reports its close once; anything it reports afterwards is ignored.
        this.generation++;
        this.onClosed();
      },
    });
  }

  private sendHello(): void {
    const info = this.opts.hello();
    const rejoin = this.session.load();
    const hello: ClientMsg = { t: 'hello', v: PROTOCOL_VERSION, name: info.name, profileId: info.profileId, cosmetics: info.cosmetics };
    if (rejoin) hello.reconnect = { code: rejoin.code, token: rejoin.token };
    this.reconnecting = rejoin !== null;
    this.transmit(JSON.stringify(hello));
  }

  private receive(data: string | ArrayBuffer): void {
    this.lastRx = this.now();
    const at = this.lastRx + this.lagMs;
    if (typeof data === 'string') {
      this.stats.bytesIn += data.length;
      const msg = parseServerMsg(data);
      if (!msg) {
        this.stats.malformed++;
        return;
      }
      this.observe(msg);
      this.enqueue(msg.t, msg, at);
      return;
    }
    const bytes = new Uint8Array(data);
    this.stats.bytesIn += bytes.byteLength;
    if (bytes.byteLength === 0 || bytes[0] !== BinMsg.SNAPSHOT) {
      this.stats.malformed++;
      return;
    }
    this.stats.snapshots++;
    const a = this.stats.snapshots === 1 ? 1 : NET.snapshotBytesEwma;
    this.stats.snapBytesAvg += a * (bytes.byteLength - this.stats.snapBytesAvg);
    this.enqueue('snapshot', bytes, at);
  }

  /** Session and handshake bookkeeping, done on arrival so it never waits for `pump()`. */
  private observe(msg: ServerMsg): void {
    switch (msg.t) {
      case 'welcome':
        this.attempt = 0;
        this.setStatus('online');
        for (const text of this.outbox.splice(0)) this.transmit(text);
        break;
      case 'joined':
        this.reconnecting = false;
        this.session.save({ code: msg.code, token: msg.token });
        break;
      case 'error':
        if (this.reconnecting) this.session.clear();
        this.reconnecting = false;
        break;
      case 'kicked':
        this.session.clear();
        this.wanted = false;
        break;
      default:
        break;
    }
  }

  private enqueue(type: keyof NetMessages, msg: unknown, at: number): void {
    this.incoming.push({ type, msg, at });
    if (this.incoming.length - this.incomingHead > NET.maxQueuedFrames) this.pump();
  }

  private startWatchdog(): void {
    if (this.watchdog) return;
    this.watchdog = setInterval(() => this.checkAlive(), NET.watchdogMs);
  }

  private stopWatchdog(): void {
    if (this.watchdog) clearInterval(this.watchdog);
    this.watchdog = null;
  }

  /**
   * Watchdog tick: online in a room with nothing received for `silenceMs`, or a handshake that
   * never reached `welcome`, drops the socket. A check that itself ran late (the tab or process
   * stalled, so frames may be waiting behind this timer) restarts the silence count instead.
   */
  private checkAlive(): void {
    const now = this.now();
    const late = now - this.lastCheck > NET.watchdogMs * NET.watchdogStallIntervals;
    this.lastCheck = now;
    if (!this.socket) return;
    if (late) {
      this.lastRx = now;
      this.openedAt = Math.max(this.openedAt, now - NET.handshakeMs / 2);
      return;
    }
    const silent = this.state === 'online' && this.session.load() !== null && now - this.lastRx > NET.silenceMs;
    const stuck = this.state !== 'online' && now - this.openedAt > NET.handshakeMs;
    if (silent || stuck) this.networkLost();
  }

  private onClosed(): void {
    this.socket = null;
    if (!this.wanted) {
      this.stopWatchdog();
      this.setStatus('offline');
      return;
    }
    const delay = NET.backoffMs[Math.min(this.attempt, NET.backoffMs.length - 1)];
    this.attempt++;
    this.setStatus('reconnecting');
    this.armReconnect(delay);
  }

  /** Opens the next socket after `delay` ms; without a network the attempt waits for one (`retryNow`). */
  private armReconnect(delay: number): void {
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.wanted) return;
      if (!this.isOnline()) {
        this.armReconnect(NET.backoffMs[NET.backoffMs.length - 1]);
        return;
      }
      this.open();
    }, delay);
  }

  private transmit(data: string | Uint8Array): void {
    const socket = this.socket;
    if (!socket) return;
    this.stats.bytesOut += typeof data === 'string' ? data.length : data.byteLength;
    if (this.lagMs === 0) {
      if (socket.isOpen) socket.send(data);
      return;
    }
    setTimeout(() => {
      if (socket.isOpen && this.socket === socket) socket.send(data);
    }, this.lagMs);
  }

  private setStatus(state: NetStatus): void {
    if (state === this.state && state !== 'reconnecting') return;
    this.state = state;
    this.opts.bus.emit('net:status', { state, attempt: this.attempt });
  }
}
